import React, { useState, useEffect } from 'react';
import { Save, Plus, Trash2, GripVertical } from 'lucide-react';
import { authApi } from '../../helpers/auth';

const AdminFooter = () => {
  const [footerData, setFooterData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    loadFooterData();
  }, []);

  const loadFooterData = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/footer.php');
      if (!response.ok) throw new Error('Erreur de chargement');
      const data = await response.json();
      setFooterData(data);
    } catch (err) {
      setError('Erreur lors du chargement des donnees du footer');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(false);

      const token = authApi.getToken();
      const response = await fetch('/api/footer.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(footerData),
      });

      if (!response.ok) throw new Error('Erreur de sauvegarde');
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError('Erreur lors de la sauvegarde');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const updateField = (path, value) => {
    setFooterData(prev => {
      const updated = JSON.parse(JSON.stringify(prev));
      const keys = path.split('.');
      let obj = updated;
      for (let i = 0; i < keys.length - 1; i++) {
        obj = obj[keys[i]];
      }
      obj[keys[keys.length - 1]] = value;
      return updated;
    });
  };

  const addLink = (section) => {
    setFooterData(prev => {
      const updated = JSON.parse(JSON.stringify(prev));
      const newLink = { label: '', url: '', external: false };
      if (section === 'bottomLinks') {
        delete newLink.external;
      }
      updated[section].push(newLink);
      return updated;
    });
  };

  const removeLink = (section, index) => {
    setFooterData(prev => {
      const updated = JSON.parse(JSON.stringify(prev));
      updated[section].splice(index, 1);
      return updated;
    });
  };

  const updateLink = (section, index, field, value) => {
    setFooterData(prev => {
      const updated = JSON.parse(JSON.stringify(prev));
      updated[section][index][field] = value;
      return updated;
    });
  };

  const moveLink = (section, index, direction) => {
    setFooterData(prev => {
      const updated = JSON.parse(JSON.stringify(prev));
      const newIndex = index + direction;
      if (newIndex < 0 || newIndex >= updated[section].length) return prev;
      const temp = updated[section][index];
      updated[section][index] = updated[section][newIndex];
      updated[section][newIndex] = temp;
      return updated;
    });
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Chargement...</span>
        </div>
      </div>
    );
  }

  if (!footerData) {
    return <div className="alert alert-danger">Impossible de charger les donnees du footer</div>;
  }

  const renderLinksEditor = (section, title) => (
    <div className="card mb-4">
      <div className="card-header d-flex justify-content-between align-items-center">
        <h5 className="mb-0">{title}</h5>
        <button className="btn btn-sm btn-outline-primary" onClick={() => addLink(section)}>
          <Plus size={16} className="me-1" /> Ajouter
        </button>
      </div>
      <div className="card-body">
        {footerData[section].map((link, index) => (
          <div key={index} className="row g-2 mb-2 align-items-center">
            <div className="col-auto">
              <div className="btn-group-vertical btn-group-sm">
                <button
                  className="btn btn-outline-secondary btn-sm px-1 py-0"
                  onClick={() => moveLink(section, index, -1)}
                  disabled={index === 0}
                >
                  <span style={{ fontSize: '10px' }}>&#9650;</span>
                </button>
                <button
                  className="btn btn-outline-secondary btn-sm px-1 py-0"
                  onClick={() => moveLink(section, index, 1)}
                  disabled={index === footerData[section].length - 1}
                >
                  <span style={{ fontSize: '10px' }}>&#9660;</span>
                </button>
              </div>
            </div>
            <div className="col">
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Label"
                value={link.label}
                onChange={(e) => updateLink(section, index, 'label', e.target.value)}
              />
            </div>
            <div className="col">
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="URL"
                value={link.url}
                onChange={(e) => updateLink(section, index, 'url', e.target.value)}
              />
            </div>
            {'external' in link && (
              <div className="col-auto">
                <div className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={link.external}
                    onChange={(e) => updateLink(section, index, 'external', e.target.checked)}
                    id={`${section}-${index}-ext`}
                  />
                  <label className="form-check-label small" htmlFor={`${section}-${index}-ext`}>
                    Externe
                  </label>
                </div>
              </div>
            )}
            <div className="col-auto">
              <button
                className="btn btn-sm btn-outline-danger"
                onClick={() => removeLink(section, index)}
              >
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
        {footerData[section].length === 0 && (
          <p className="text-muted mb-0">Aucun lien. Cliquez sur "Ajouter" pour en creer un.</p>
        )}
      </div>
    </div>
  );

  return (
    <div className="container-fluid py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Footer</h2>
        <div className="d-flex align-items-center gap-2">
          {success && <span className="text-success">Sauvegarde reussie !</span>}
          {error && <span className="text-danger">{error}</span>}
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            <Save size={16} className="me-2" />
            {saving ? 'Sauvegarde...' : 'Sauvegarder'}
          </button>
        </div>
      </div>

      {/* Description */}
      <div className="card mb-4">
        <div className="card-header"><h5 className="mb-0">Description</h5></div>
        <div className="card-body">
          <textarea
            className="form-control"
            rows={3}
            value={footerData.description}
            onChange={(e) => updateField('description', e.target.value)}
          />
        </div>
      </div>

      {/* Liens rapides */}
      {renderLinksEditor('quickLinks', 'Liens rapides')}

      {/* Informations */}
      {renderLinksEditor('infoLinks', 'Informations')}

      {/* Horaires */}
      <div className="card mb-4">
        <div className="card-header"><h5 className="mb-0">Horaires</h5></div>
        <div className="card-body">
          <div className="mb-3">
            <label className="form-label">Jours d'ouverture</label>
            <input
              type="text"
              className="form-control"
              value={footerData.schedule.openDays}
              onChange={(e) => updateField('schedule.openDays', e.target.value)}
            />
          </div>
          <div>
            <label className="form-label">Jours de fermeture</label>
            <input
              type="text"
              className="form-control"
              value={footerData.schedule.closedDays}
              onChange={(e) => updateField('schedule.closedDays', e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Contact */}
      <div className="card mb-4">
        <div className="card-header"><h5 className="mb-0">Contact</h5></div>
        <div className="card-body">
          <div className="mb-3">
            <label className="form-label">Adresse</label>
            <input
              type="text"
              className="form-control"
              value={footerData.contact.address}
              onChange={(e) => updateField('contact.address', e.target.value)}
            />
          </div>
          <div className="mb-3">
            <label className="form-label">Telephone</label>
            <input
              type="text"
              className="form-control"
              value={footerData.contact.phone}
              onChange={(e) => updateField('contact.phone', e.target.value)}
            />
          </div>
          <div>
            <label className="form-label">Email</label>
            <input
              type="email"
              className="form-control"
              value={footerData.contact.email}
              onChange={(e) => updateField('contact.email', e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Liens du bas */}
      {renderLinksEditor('bottomLinks', 'Liens du bas (copyright)')}
    </div>
  );
};

export default AdminFooter;
